import React, { useState } from "react";
import { HeaderFrame, HEADER_ROW_HEIGHT } from "./HeaderFrame";
import { ForYouDropdown } from "./ForYouDropdown";
import { ProfileDropdown } from "./ProfileDropdown";
import { LegacyNavigationPane } from "src/ccl/blocks/navigationPane";
import { Icon, Logo } from "src/ccl/document";
import {
  HeaderLink,
  SupportLauncher,
  Link,
  LinkButton,
} from "src/ccl/navigation";
import { sendToAmplitude } from "src/utils/analytics";
import { UserKindContext } from "src/hooks";
import { styled } from "src/ccl/stitches";
import { Box } from "src/ccl/layout";

const Wrapper = styled("header", {
  height: HEADER_ROW_HEIGHT.mobile,
  position: "relative",
  zIndex: "$500",

  "@bp2": {
    height: HEADER_ROW_HEIGHT.desktop,
  },
});

const TopLinkContainer = styled("div", {
  display: "flex",
  alignItems: "center",

  "@bp2": {
    minHeight: "$avatar",
  },
});

const DesktopHeaderLink = styled(HeaderLink, {
  display: "none",
  minHeight: "$12",

  "@bp2": {
    alignItems: "center",
    display: "flex",
  },
});

const MobileNavigationPaneLauncher = styled("button", {
  alignItems: "center",
  background: "transparent",
  border: 0,
  display: "flex",
  marginLeft: "$5",
  minHeight: "$12",
  minWidth: "$12",
  p: 0,
  cursor: "pointer",

  "@bp2": {
    display: "none",
  },
});

interface LegacyHeaderProps {
  context: UserKindContext;
  avatarImageUrl?: string;
  avatarInitials?: string;
  userName?: string;
  userEmail?: string;
  onSignOut?: () => void;
  onMouseEnter?: () => void;
  fullPageHeaderChildren?: React.ReactNode;
}

export const LegacyHeader = ({
  context,
  avatarImageUrl,
  avatarInitials,
  userName,
  userEmail,
  onSignOut,
  onMouseEnter,
  fullPageHeaderChildren,
}: LegacyHeaderProps) => {
  const [navigationPaneOpen, setNavigationPaneOpen] = useState(false);

  return (
    <>
      <LegacyNavigationPane
        open={navigationPaneOpen}
        onClose={() => setNavigationPaneOpen(false)}
        onSignOut={() => {
          onSignOut && onSignOut();
          setNavigationPaneOpen(false);
        }}
        userName={userName}
        userEmail={userEmail}
        context={context}
      />
      <Wrapper data-test-id="Header">
        <HeaderFrame>
          <Link
            data-test-id="HeaderLogo"
            css={{
              alignItems: "center",
              display: "none",
              minHeight: "$12",
              minWidth: "$12",
              "@bp2": { display: "flex" },
            }}
            to={context === "signedOut" ? "/" : "/dashboard"}
            onClick={() =>
              sendToAmplitude("top nav - clicks contact home button")
            }
          >
            <Logo variant="full" />
          </Link>

          <Link
            data-test-id="HeaderMark"
            css={{
              alignItems: "center",
              display: "flex",
              minHeight: "$12",
              minWidth: "$12",
              "@bp2": { display: "none" },
            }}
            to={context === "signedOut" ? "/" : `/dashboard`}
            onClick={() =>
              sendToAmplitude("top nav - clicks contact home button")
            }
          >
            <Logo variant="mark" />
          </Link>

          <TopLinkContainer>
            <ForYouDropdown context={context} />

            {context === "signedOut" && (
              <DesktopHeaderLink
                data-test-id="HeaderPodcast"
                css={{
                  display: "none",
                  "@bp2": { display: "flex", alignItems: "center" },
                }}
                to="/creative-paths"
              >
                <Icon
                  variant="creativePathsVertical"
                  size={16}
                  css={{
                    mr: "$2",
                    circle: {
                      fill: "$black",
                    },
                  }}
                />
                Podcast
              </DesktopHeaderLink>
            )}
            <SupportLauncher
              data-test-id="HeaderNeedHelp"
              longContent="Need help?"
              color="black"
              iconSize={16}
              css={{
                display: "none",
                marginRight: "$11",
                "@bp2": { display: "block" },
                "@bp3": { display: "none" },
              }}
              longContentCSS={{
                marginRight: "$11",
              }}
              iconCss={{
                "@bp2": { display: "block" },
              }}
              fallbackUrl={
                context === "talent" ? "mailto:help@contact.xyz" : undefined
              }
            />

            {context === "signedOut" && (
              <DesktopHeaderLink
                data-test-id="HeaderSignIn"
                to="/sign-in"
                css={{
                  display: "flex",
                  alignItems: "center",
                  mr: "$5",
                  "@bp2": { mr: "$9" },
                }}
              >
                <Icon
                  variant="user"
                  size={16}
                  css={{
                    display: "none",
                    "@bp2": { display: "block", mr: "$2" },
                  }}
                />
                Sign in
              </DesktopHeaderLink>
            )}
            {fullPageHeaderChildren ? (
              <> {fullPageHeaderChildren} </>
            ) : (
              <>
                <LinkButton
                  data-test-id="HeaderDashboardCTA"
                  variant="header"
                  css={{ minHeight: "$12" }}
                  to={
                    context === "client" || context === "signedOut"
                      ? "/jobs/new"
                      : "/dashboard"
                  }
                  onClick={() =>
                    (context === "client" || context === "signedOut") &&
                    sendToAmplitude("top nav - clicks book talent")
                  }
                  onMouseEnter={() =>
                    (context === "client" || context === "signedOut") &&
                    onMouseEnter &&
                    onMouseEnter()
                  }
                >
                  {context === "client" || context === "signedOut"
                    ? "Book Creatives"
                    : "Dashboard"}
                  <Icon variant="chevronRight" size={12} css={{ ml: "$4" }} />
                </LinkButton>

                <MobileNavigationPaneLauncher
                  aria-label="Open navigation"
                  data-test-id="LegacyHeaderMobileBurger"
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();

                    setNavigationPaneOpen(true);
                  }}
                >
                  <Icon
                    size={30}
                    variant="burgerSquare"
                    color="grey2"
                    css={{
                      path: {
                        fill: "$black",
                      },
                    }}
                  />
                </MobileNavigationPaneLauncher>

                {context !== "signedOut" && (
                  <Box css={{ marginLeft: "$11" }}>
                    <ProfileDropdown
                      avatarImageUrl={avatarImageUrl}
                      avatarInitials={avatarInitials}
                      onSignOut={onSignOut}
                      userName={userName}
                      userEmail={userEmail}
                      context={context}
                    />
                  </Box>
                )}
              </>
            )}
          </TopLinkContainer>
        </HeaderFrame>
      </Wrapper>
    </>
  );
};
